import * as React from 'react'
import { useTrackToggle } from '@livekit/components-react'
import { CaptureOptionsBySource, ToggleSource } from '@livekit/components-core'
import {
  KeyboardVoiceOutlinedIcon,
  MicOffOutlinedIcon,
  VideocamOffOutlinedIcon,
  VideocamOutlinedIcon,
} from '@common/icons'
import { IconButton, IconButtonProps } from '@common/components'
import { TrackPublishOptions } from 'livekit-client'

export interface TrackToggleProps<T extends ToggleSource>
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  source: T
  initialState?: boolean
  onChange?: (enabled: boolean, isUserInitiated: boolean) => void
  captureOptions?: CaptureOptionsBySource<T>
  publishOptions?: TrackPublishOptions
  onDeviceError?: (error: Error) => void
}

export const TrackToggleComponent: <T extends ToggleSource>(
  props: TrackToggleProps<T> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode = React.forwardRef(function TrackToggle<T extends ToggleSource>(
  { ...props }: TrackToggleProps<T>,
  ref: React.Ref<HTMLButtonElement>,
) {
  const { buttonProps, enabled } = useTrackToggle(props)
  return (
    <IconButton ref={ref} {...(buttonProps as IconButtonProps)}>
      {getSourceIcon(props.source, props.disabled ? false : enabled)}
    </IconButton>
  )
})

export function getSourceIcon(source: ToggleSource, enabled: boolean) {
  switch (source) {
    case 'microphone':
      return enabled ? <KeyboardVoiceOutlinedIcon /> : <MicOffOutlinedIcon />
    case 'camera':
      return enabled ? <VideocamOutlinedIcon /> : <VideocamOffOutlinedIcon />
    default:
      return undefined
  }
}
